import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, ControlContainer, FormGroupDirective } from '@angular/forms';

import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import Quill from 'quill';
import ImageResize from 'quill-image-resize-module'
import { QuillModules } from 'ngx-quill/lib/quill-editor.interfaces';

import { FormValidationService } from '@services/form-validation.service';
import { QuillEvent, QuillSelectionEvent } from '@models/editor';

Quill.register('modules/imageResize', ImageResize);

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  viewProviders: [{
    provide: ControlContainer,
    useExisting: FormGroupDirective
  }]
})
export class EditorComponent implements OnInit, OnDestroy {
  @Input() public label: string = 'Label';
  @Input() public toolbar: boolean = true;
  @Input() public controlName!: string;
  @Input() public autofocus: boolean = false;
  @Input() public needValidation: boolean = false;

  @Output() public editorFocused: EventEmitter<void> = new EventEmitter<void>();

  public modules: QuillModules;

  public editorControl!: AbstractControl;
  public isValid: boolean = true;
  public errorMsg: string = '';

  private editor: Quill | null = null;
  private selection: { index: number; length: number } | null = null;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(private parentGroup: FormGroupDirective, private validationService: FormValidationService) {
    this.setUpEditor();
  }

  ngOnInit(): void {
    this.editorControl = this.parentGroup.form.get(this.controlName);

    if (!this.toolbar) {
      this.modules = {
        ...this.modules,
        toolbar: false
      };
    }

    this.editorControl.statusChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((): boolean => this.needValidation)
      )
      .subscribe((): void => this.validate());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onEditorCreated(editor: Quill): void {
    this.editor = editor;

    if (this.autofocus) {
      editor.focus();
    }
  }

  public insertPlainText(text: string): void {
    if (!text) {
      return;
    }

    if (!this.editor) {
      const current: string = this.editorControl?.value || '';
      this.editorControl.setValue(`${current}${text}`);
      this.editorControl.markAsDirty();
      return;
    }

    const range = this.selection || this.editor.getSelection(),
          end: number = Math.max(this.editor.getLength() - 1, 0),
          index: number = range?.index ?? end,
          selectionLength: number = range?.length || 0;

    if (selectionLength) {
      this.editor.deleteText(index, selectionLength, 'user');
    }
    
    this.editor.insertText(index, text, 'user');
    this.editor.setSelection(index + text.length, 0, 'silent');
    this.selection = {index: index + text.length, length: 0};
    this.editor.focus();
  }

  public onFocus(event: QuillEvent): void {
    this.editorFocused.emit();

    if (this.needValidation) {
      this.validate();
    }
  }

  public onSelectionChanged(event: QuillSelectionEvent): void {
    if (event.range) {
      this.selection = event.range;
    }
  }

  public onBlur(event: QuillEvent): void {
    this.editorControl.markAsTouched();

    if (this.needValidation) {
      this.validate();
    }
  }

  private setUpEditor(): void {
    this.modules = {
      imageResize: { }
    };
  }

  private validate(): void {
    this.isValid = !(this.needValidation
      && this.editorControl.invalid
      && (this.editorControl.dirty || this.editorControl.touched));

    if (!this.isValid) {
      this.errorMsg = this.validationService.getControlErrorMessage(this.editorControl, this.controlName);
    }
  }
}
